/**
 * Parsley Health — Customer.io Analytics
 *
 * Mirrors the same events tracked in posthog-analytics.js:
 *   - Customer identification (logged-in users)
 *   - product_viewed
 *   - collection_viewed
 *   - cart_viewed
 *   - add_to_cart        (intercepts fetch to /cart/add.js)
 *   - checkout_started   (cart form submit)
 *   - search_performed   (search form submit)
 *
 * Data is provided by window.__posthogData, injected via Liquid in theme.liquid.
 * (We reuse the same data object since the page context is identical.)
 */

(function () {
  'use strict';

  // Customer.io must be initialized before this file runs
  if (typeof window._cio === 'undefined') {
    console.warn('[Customer.io] _cio not found — analytics disabled.');
    return;
  }

  var data = window.__posthogData || {};

  // ─── Customer Identification ─────────────────────────────────────────────────

  function getAnonId() {
    var key = 'cio_anon_id';
    var id = localStorage.getItem(key);
    if (!id) {
      id = 'anon_' + crypto.randomUUID();
      localStorage.setItem(key, id);
    }
    return id;
  }

  function identifyCustomer() {
    var customer = data.customer;

    if (customer && customer.id) {
      var anonId = localStorage.getItem('cio_anon_id');

      _cio.identify({
        id: String(customer.id),
        email: customer.email,
        first_name: customer.firstName,
        last_name: customer.lastName,
        orders_count: customer.ordersCount,
        total_spent: customer.totalSpent,
        anonymous_id: anonId || undefined,
      });

      if (anonId) localStorage.removeItem('cio_anon_id');
    } else {
      _cio.identify({ id: getAnonId() });
    }
  }

  // ─── Product Viewed ──────────────────────────────────────────────────────────

  function trackProductViewed() {
    if (data.pageType !== 'product' || !data.product) return;

    var p = data.product;
    _cio.track('product_viewed', {
      product_id: p.id,
      product_title: p.title,
      product_type: p.type,
      product_vendor: p.vendor,
      product_url: p.url,
      price: p.price,
      compare_at_price: p.compareAtPrice,
      variant_id: p.selectedVariantId,
      variant_title: p.selectedVariantTitle,
      sku: p.selectedVariantSku,
    });
  }

  // ─── Collection Viewed ───────────────────────────────────────────────────────

  function trackCollectionViewed() {
    if (data.pageType !== 'collection' || !data.collection) return;

    _cio.track('collection_viewed', {
      collection_id: data.collection.id,
      collection_title: data.collection.title,
      collection_handle: data.collection.handle,
    });
  }

  // ─── Cart Viewed ─────────────────────────────────────────────────────────────

  function trackCartViewed() {
    if (data.pageType !== 'cart') return;

    _cio.track('cart_viewed', {
      cart_total: data.cartTotal,
    });
  }

  // ─── Add to Cart ─────────────────────────────────────────────────────────────
  // Intercepts fetch calls to /cart/add.js so we capture regardless of
  // which theme component (product form, quick-buy, cart drawer) triggers it.

  function interceptAddToCart() {
    var originalFetch = window.fetch;

    window.fetch = function (url, options) {
      var promise = originalFetch.apply(this, arguments);

      var urlStr = (typeof url === 'string') ? url : (url && url.url) || '';
      if (!urlStr.includes('/cart/add')) return promise;

      promise.then(function (response) {
        if (!response.ok) return;

        response.clone().json().then(function (body) {
          var items = [];
          if (body.items_added) {
            items = body.items_added;
          } else if (Array.isArray(body.items)) {
            items = body.items;
          } else if (body.variant_id) {
            items = [body];
          }

          items.forEach(function (item) {
            if (!item || !item.variant_id) return;

            _cio.track('add_to_cart', {
              product_id: item.product_id,
              variant_id: item.variant_id,
              product_title: item.product_title || item.title,
              variant_title: item.variant_title,
              price: (item.price / 100).toFixed(2),
              quantity: item.quantity,
              sku: item.sku,
              vendor: item.vendor,
            });
          });
        }).catch(function () {});
      }).catch(function () {});

      return promise;
    };
  }

  // ─── Checkout Started ────────────────────────────────────────────────────────

  function trackCheckoutStarted() {
    // Cart form submissions — covers both the cart page and cart drawer
    document.addEventListener('submit', function (event) {
      var form = event.target;
      if (!form || form.tagName !== 'FORM') return;

      var hasCheckoutButton = !!form.querySelector('[name="checkout"]');
      var actionIsCart = (form.getAttribute('action') || '').includes('/cart');
      if (!hasCheckoutButton && !actionIsCart) return;

      _cio.track('checkout_started', { cart_total: data.cartTotal });
    });

    // Express checkout / "Buy it now" links that go directly to /checkout
    document.addEventListener('click', function (event) {
      var target = event.target;
      while (target && target !== document.body) {
        if (target.tagName === 'A' && target.href && target.href.includes('/checkout')) {
          _cio.track('checkout_started', { cart_total: data.cartTotal });
          break;
        }
        target = target.parentElement;
      }
    }, { passive: true });
  }

  // ─── Search Performed ────────────────────────────────────────────────────────

  function trackSearch() {
    document.addEventListener('submit', function (event) {
      var form = event.target;
      if (!form || form.tagName !== 'FORM') return;

      var action = form.getAttribute('action') || '';
      if (!action.includes('/search') && form.getAttribute('role') !== 'search') return;

      var input = form.querySelector('[name="q"]');
      var query = input ? input.value.trim() : '';
      if (!query) return;

      _cio.track('search_performed', { query: query });
    }, { passive: true });
  }

  // ─── Init ────────────────────────────────────────────────────────────────────

  function init() {
    identifyCustomer();
    trackProductViewed();
    trackCollectionViewed();
    trackCartViewed();
    interceptAddToCart();
    trackCheckoutStarted();
    trackSearch();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
